import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import forwardRefWithTranslation from '@/common/forwardRefWithTranslation';
import {
    getCountryAndNationalPhoneNumberFromRawPhoneNumber,
    getE164PhoneNumberFromCountryAndNationalPhoneNumber,
} from '@/common/helpers/phone_number';
import { isGiftCardEmpty  as isGiftCardEmptyFn } from '@/common/customer.gift-card';
import AddGiftCardButton from '@/common/customer.gift-card/AddGiftCardButton';
import AdditionalDataFieldsToCapture from "@/common/customer.additional-data-fields";
import changeToAddGiftCard from '../customer.gift-card';

import ModalHeader from '../common/modal/header';

import {
    PhoneNumberInput,
    PhoneNumberInputWithValidation,
} from '../common/inputs/phone-number';
import CustomHtmlSlot from '@/common/components/CustomHtmlSlot';
import ProfileDeletionModal from "@/desktop/customer.details/ProfileDeletionModal";

const {
    Customer,
    UI,
    Order,
    headoffice: {
        customer_phone_number_validation_enabled,
        facebook_sign_in_enabled,
    },
} = Mobi2Go;

const base_class = 'm2g-customer-details';
const baseClass = (class_name = '') => `${base_class}${class_name}`;

const displayProfileDeletionModal = () => {
    const modal = UI.ExternalModal.create(
        'profile-deletion',
        <ProfileDeletionModal
            closeModal={() => modal.close(true)}
            headoffice={Mobi2Go.headoffice}
        />,
        {
            'data-view': 'profile-deletion',
            className: 'm2g-profile-deletion',
        }
    );

    modal.mount();
    modal.open();
};

export default forwardRefWithTranslation('customer.details')(
    class CustomerDetails extends React.Component {
        saveToServer = _.debounce(() => {
            const {
                name,
                is_subscriber,
                phone_number,
                phone_number_country,
            } = this.state;
            const { setModalIsClosable } = this.props;

            let phone_number_validated = phone_number;

            if (customer_phone_number_validation_enabled) {
                try {
                    phone_number_validated = getE164PhoneNumberFromCountryAndNationalPhoneNumber(
                        phone_number_country,
                        phone_number
                    );
                } catch (e) {
                    this.handleUpdateError('Invalid phone_number number');
                    return;
                }
            }

            if (name === '' || phone_number_validated === '') {
                setModalIsClosable(false);
                return;
            }

            try {
                Customer.update(
                    {
                        name,
                        phone_number: phone_number_validated,
                        is_subscriber,
                    },
                    (status, error) => {
                        if (status === 'error' && error) {
                            this.handleUpdateError(error);
                        } else {
                            this.updateCustomerState();
                        }
                    }
                );
            } catch (error) {
                this.updateCustomerState();
            }
        }, 400);

        static propTypes = {
            t: PropTypes.func.isRequired,
            closeModal: PropTypes.func.isRequired,
            setModalIsClosable: PropTypes.func.isRequired,
        };

        constructor() {
            super();
            this.state = {
                email: '',
                name: '',
                phone_number: '',
                phone_number_country: Mobi2Go.headoffice.country_iso3166_alpha2,
                phone_number_error_message: '',
                is_subscriber: false,
                is_guest: false,
                isGiftCardEmpty: false,
                loyalty_program: {},
                loyalty_program_with_gift_cards: '',
            };
            Customer.bind({
                logIn: this.updateCustomerState,
                update: this.updateCustomerState,
                updateEmail: this.updateCustomerState,
            });
        }

        componentDidMount = () => {
            this.updateCustomerState();
            this.updateOrderState();
        };

        updateCustomerState = () => {
            const { setModalIsClosable } = this.props;
            let phone_number = Customer.getPhoneNumber();
            let phone_number_country =
                Mobi2Go.headoffice.country_iso3166_alpha2;
            let phone_number_is_valid = true;

            setModalIsClosable(true);

            if (phone_number && customer_phone_number_validation_enabled) {
                try {
                    [
                        phone_number,
                        phone_number_country,
                    ] = getCountryAndNationalPhoneNumberFromRawPhoneNumber(
                        Customer.getPhoneNumber()
                    );
                    // Will throw if phone number is invalid
                    getE164PhoneNumberFromCountryAndNationalPhoneNumber(
                        phone_number_country,
                        phone_number
                    );
                } catch (e) {
                    // Use default country and raw phone number
                    phone_number_is_valid = false;
                }
            }

            this.setState(
                {
                    email: Customer.getEmail(),
                    name: Customer.getName(),
                    phone_number,
                    phone_number_country,
                    phone_number_error_message: '',
                    is_subscriber: Customer.isSubscriber(),
                    is_guest: Customer.isGuest(),
                    isGiftCardEmpty: isGiftCardEmptyFn(Customer),
                    loyalty_program_with_gift_cards: Customer.getEnabledLoyaltyProgramWithGiftCardsEnabled(),
                },
                () => {
                    if (!phone_number_is_valid) {
                        this.handleUpdateError('Invalid phone_number number');
                    }
                }
            );
        };

        onNameChange = e => {
            this.setState({ name: e.target.value }, this.saveToServer);
        };

        onPhoneNumberChange = phone_number => {
            this.setState({ phone_number }, () => {
                if (customer_phone_number_validation_enabled) {
                    this.saveToServer();
                }
            });
        };

        onPhoneNumberCountryChange = phone_number_country => {
            this.setState({ phone_number_country }, this.saveToServer);
        };

        onPhoneNumberBlur = () => {
            this.saveToServer();
        };

        onSubscriberYesClick = () => {
            this.setState({ is_subscriber: true });
            this.saveToServer();
        };

        onSubscriberNoClick = () => {
            this.setState({ is_subscriber: false });
            this.saveToServer();
        };

        onChangePasswordClick = () => {
            const retain_overlay = false;
            const { closeModal } = this.props;
            closeModal(retain_overlay);
            UI.trigger('showCustomerPasswordUpdater');
        };

        onChangeEmailClick = () => {
            const retain_overlay = false;
            const { closeModal } = this.props;
            closeModal(retain_overlay);
            UI.trigger('showCustomerEmailUpdater');
        };

        onLogOutClick = () => {
            const { closeModal } = this.props;
            closeModal();

            if (facebook_sign_in_enabled) {
                FB.getLoginStatus(function(response) {
                    if (response.status === 'connected') {
                        FB.logout(function(response) {
                            Customer.logOut();
                        });
                    } else {
                        Customer.logOut();
                    }
                }, true);

                return;
            }

            Customer.logOut();
        };

        handleUpdateError(error) {
            const { phone_number_error_message: current_error } = this.state;
            const { setModalIsClosable } = this.props;
            const { t } = this.props;
            if (error === 'Invalid phone_number number') {
                this.setState(
                    {
                        phone_number_error_message: t(
                            'customer:update_invalid_phone_title'
                        ),
                    },
                    () => {
                        if (current_error && this.phone_number_error_ref) {
                            UI.helpers.setAnimation(
                                this.phone_number_error_ref,
                                'rock',
                                true
                            );
                        }
                    }
                );
                setModalIsClosable(false);
            }
        }

        isLoyaltyProgramUsed(loyaltyProgram) {
            if (
                Object.keys(loyaltyProgram).length === 1 &&
                Object.keys(loyaltyProgram).includes('PunchCard')
            ) {
                return false;
            }
            return Object.keys(loyaltyProgram).length > 0;
        }

        updateOrderState() {
            this.setState({
                loyalty_program: Order.getLoyalty(),
            });
        }

        render = () => {
            if (!Customer.isLoggedIn()) {
                return null;
            }
            const { t, closeModal } = this.props;
            const {
                is_guest,
                name,
                phone_number,
                phone_number_country,
                phone_number_error_message,
                email,
                is_subscriber,
                isGiftCardEmpty,
                loyalty_program,
                loyalty_program_with_gift_cards
            } = this.state;

            return (
                <div className={baseClass('-modal')}>
                    <ModalHeader
                        headerText={
                            is_guest
                                ? t('your_account_guest')
                                : t('your_account')
                        }
                        onClose={closeModal}
                    />
                    <div className={baseClass('-container m2g-modal-body')}>
                        <CustomHtmlSlot slot={'customer-details-modal'} />
                        <div
                            className={baseClass('-name m2g-input-container')}
                            data-icon-before
                        >
                            <input
                                type="text"
                                value={name}
                                placeholder={t('full_name_placeholder')}
                                className={baseClass('-name-input m2g-input')}
                                onChange={this.onNameChange}
                            />
                            <span className="m2g-icon m2g-icon--user" />
                        </div>
                        {phone_number &&
                        customer_phone_number_validation_enabled ? (
                            <PhoneNumberInputWithValidation
                                value={phone_number}
                                country={phone_number_country}
                                placeholder={t('phone_number_placeholder')}
                                error={phone_number_error_message}
                                onChange={this.onPhoneNumberChange}
                                onCountryChange={
                                    this.onPhoneNumberCountryChange
                                }
                                containerClassName="m2g-customer-details-phone-number"
                                className="m2g-customer-details-phone-number-input"
                                errorRef={ref => {
                                    this.phone_number_error_ref = ref;
                                }}
                            />
                        ) : (
                            <PhoneNumberInput
                                value={phone_number}
                                placeholder={t('phone_number_placeholder')}
                                onChange={this.onPhoneNumberChange}
                                onBlur={this.onPhoneNumberBlur}
                                containerClassName="m2g-customer-details-phone-number"
                                className="m2g-customer-details-phone-number-input"
                            />
                        )}

                        <AdditionalDataFieldsToCapture closeModal={this.props.closeModal} />

                        {!this.isLoyaltyProgramUsed(loyalty_program) && (
                            <div className={baseClass('-subscriber')}>
                                <span
                                    className={baseClass(
                                        '-subscriber-label m2g-text--small'
                                    )}
                                >
                                    {t('customer:subscriber_question')}
                                </span>
                                <div
                                    className={baseClass('-subscriber-buttons')}
                                >
                                    <button
                                        type="button"
                                        data-checked={is_subscriber}
                                        className={baseClass(
                                            '-subscriber-yes-button m2g-button m2g-button--bare'
                                        )}
                                        onClick={this.onSubscriberYesClick}
                                    >
                                        <span className="m2g-icon m2g-icon--checkbox m2g-icon--tick" />
                                        <span>{t('yes')}</span>
                                    </button>
                                    <button
                                        type="button"
                                        data-checked={!is_subscriber}
                                        className={baseClass(
                                            '-subscriber-no-button m2g-button m2g-button--bare'
                                        )}
                                        onClick={this.onSubscriberNoClick}
                                    >
                                        <span className="m2g-icon m2g-icon--checkbox m2g-icon--tick" />
                                        <span>{t('no')}</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {!is_guest && (isGiftCardEmpty || loyalty_program_with_gift_cards) && (
                            <AddGiftCardButton
                                onClick={() => {
                                    closeModal();
                                    changeToAddGiftCard();
                                }}
                                classNames={{
                                    button:
                                        'm2g-button m2g-button--bare m2g-add-gift-card-button',
                                }}
                            />
                        )}

                        {!is_guest && (
                            <>
                                <button
                                    type="button"
                                    key="change-password-button"
                                    className={baseClass(
                                        '-change-password-button m2g-button m2g-button--bare'
                                    )}
                                    onClick={this.onChangePasswordClick}
                                >
                                    {t('change_password_button')}
                                </button>
                                <button
                                    type="button"
                                    key="change-email-button"
                                    className={baseClass(
                                        '-change-email-button m2g-button m2g-button--bare'
                                    )}
                                    onClick={this.onChangeEmailClick}
                                >
                                    {t('change_email_button')}
                                </button>
                                <button
                                    key="request-account-delete-button"
                                    className={baseClass(
                                        '-account-delete-button m2g-button m2g-button--bare'
                                    )}
                                    onClick={displayProfileDeletionModal}
                                >
                                    {t('delete_account_button')}
                                </button>
                            </>
                        )}

                        <div
                            className={baseClass(
                                '-email-display m2g-text--small m2g-text--offset'
                            )}
                        >
                            {email}
                        </div>

                        <button
                            type="button"
                            className={baseClass(
                                '-log-out-button m2g-button m2g-button--bare'
                            )}
                            onClick={this.onLogOutClick}
                        >
                            {t('logout_button')}
                        </button>
                    </div>
                </div>
            );
        };
    }
);
